<template>
  <div class="space-y-3" data-testid="device-model-section">
    <div v-if="heading !== 'hide'" class="flex items-start justify-between gap-2">
      <div>
        <p class="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
          {{ tt('3D model', '3D model') }}
        </p>
        <p class="text-[11px] text-gray-400 mt-0.5">
          {{
            tt(
              'Saved on the device — it renders in the Home 3D view wherever this device is placed.',
              'Shranjeno na napravi — izriše se v pogledu 3D tam, kjer je ta naprava postavljena.',
            )
          }}
        </p>
      </div>
    </div>

    <!-- Library -->
    <div class="space-y-2">
      <!--
        The families of THIS device's class (card t_d267b30d): a cover is offered
        the shade families, a LIGHT the luminaire family — the owner's lamp was
        offered the shade list, which is the report this section answers. The
        heading names the list so it can never read as "shades, for a lamp".
      -->
      <p class="text-[11px] text-gray-400" data-testid="device-model-library-group">
        {{ libraryGroupLabel }}
      </p>
      <div class="grid grid-cols-3 gap-1.5" data-testid="device-model-library">
        <!-- No model: the device keeps drawing as its own symbol. It is the
             first tile of the same grid, the way the drawing pickers offer
             "Automatic" beside the shapes. -->
        <button
          type="button"
          class="flex flex-col items-center gap-1 rounded-lg border px-1.5 py-2 text-center transition-colors"
          :class="
            source === 'none'
              ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
              : 'border-gray-300 dark:border-gray-600 hover:border-blue-400'
          "
          data-testid="device-model-source-none"
          @click="pickSource('none')">
          <span class="material-symbols-outlined text-[22px] leading-none text-gray-500 dark:text-gray-300">block</span>
          <span class="text-[11px] leading-tight text-gray-800 dark:text-gray-100">{{
            tt('No model', 'Brez modela')
          }}</span>
          <span class="text-[10px] leading-none text-gray-400">{{ tt('the symbol', 'simbol') }}</span>
        </button>
        <button
          v-for="row in libraryRows"
          :key="row.key"
          type="button"
          class="flex flex-col items-center gap-1 rounded-lg border px-1.5 py-2 text-center transition-colors"
          :class="
            rowActive(row)
              ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
              : 'border-gray-300 dark:border-gray-600 hover:border-blue-400'
          "
          :data-testid="row.testid"
          @click="pickRow(row)">
          <span class="material-symbols-outlined text-[22px] leading-none text-gray-500 dark:text-gray-300">{{
            row.icon
          }}</span>
          <span class="text-[11px] leading-tight text-gray-800 dark:text-gray-100">{{
            tt(row.titleEn, row.titleSl)
          }}</span>
          <span class="text-[10px] leading-none text-gray-400">{{ row.modelLabel }}</span>
        </button>
      </div>

      <template v-if="familyType">
        <!-- Colour — one language for every family (the house's RAL palette). -->
        <div>
          <label class="block text-xs font-medium text-gray-600 dark:text-gray-300 mb-1">{{ colourLabel }}</label>
          <div class="flex flex-wrap gap-1.5" data-testid="device-model-colors">
            <button
              v-for="tone in ralTones"
              :key="tone.code"
              type="button"
              class="w-7 h-7 rounded-full border-2 transition-transform"
              :class="prefs.color === tone.code ? 'border-blue-500 scale-110' : 'border-gray-300 dark:border-gray-600'"
              :style="{ backgroundColor: cssOf(tone.rgb) }"
              :title="`RAL ${tone.code} — ${tt(tone.en, tone.sl)}`"
              :data-testid="`device-model-ral-${tone.code}`"
              @click="setPref('color', tone.code)" />
          </div>
          <p class="text-[11px] text-gray-400 mt-1">
            RAL {{ prefs.color }} — {{ tt(activeTone?.en ?? '', activeTone?.sl ?? '') }}
          </p>
        </div>

        <!-- LUMINAIRE: which fixture, its own size table, the accent tone. -->
        <template v-if="isLuminaire">
          <label class="block text-xs text-gray-600 dark:text-gray-300">
            {{ tt('Fixture', 'Svetilo') }}
            <select
              :value="luminaireVariant"
              class="mt-1 w-full px-2 py-1 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-xs"
              data-testid="device-model-variant"
              @change="setVariant(($event.target as HTMLSelectElement).value)">
              <option v-for="variant in luminaireVariants" :key="variant" :value="variant">
                {{ tt(variantTitles[variant].en, variantTitles[variant].sl) }}
              </option>
            </select>
            <span class="text-[11px] text-gray-400">{{
              tt(
                'The house’s own parametric fixture — it lights up with the device, it does not hang by a position.',
                'Hišino lastno parametrično svetilo — zasveti z napravo, ne premika se po položaju.',
              )
            }}</span>
          </label>

          <!--
            The MOUNTING SPOT (card t_0b96786f) — the owner's five words:
            *"seiling, hanging, wall top, wall bottom, standing"*. A mount is not
            an offset: every variant's origin IS its mount (`bimLuminaireParts`),
            so the spot chooses the POINT in the room and the NORMAL it faces,
            and the fixture keeps its own geometry. Beside the select sits the
            consequence the operator is about to save — the resolved height.
          -->
          <div class="grid grid-cols-2 gap-2" data-testid="device-model-mount">
            <label class="text-xs text-gray-600 dark:text-gray-300">
              {{ tt('Mount', 'Namestitev') }}
              <select
                :value="mount"
                :disabled="!mountRoom"
                class="mt-1 w-full px-2 py-1 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-xs disabled:opacity-60"
                data-testid="device-model-mount-select"
                @change="setMount(($event.target as HTMLSelectElement).value)">
                <option v-for="option in mountOptions" :key="option.value" :value="option.value">
                  {{ tt(option.en, option.sl) }}
                </option>
              </select>
            </label>
            <div class="text-xs text-gray-600 dark:text-gray-300">
              <span class="block">{{ tt('Position', 'Položaj') }}</span>
              <span
                class="mt-1 block px-2 py-1 rounded-lg bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-100"
                data-testid="device-model-mount-summary">
                {{ mountSummary }}
              </span>
            </div>
          </div>
          <p
            v-if="!mountRoom && !mountRoomsLoading"
            class="text-[11px] text-amber-500"
            data-testid="device-model-mount-hint">
            {{ tt('This device is not linked to a room', 'Ta naprava ni povezana s prostorom') }}
          </p>

          <!--
            The LIGHT itself (tier 1): colour temperature and beam. The fixture
            glows in this colour and now also throws a real light with this cone,
            so a 2700 K pendant reads warm on the wall and a 24° spot pools on the
            floor instead of washing the whole room.
          -->
          <div class="grid grid-cols-2 gap-2" data-testid="device-model-light">
            <label class="text-xs text-gray-600 dark:text-gray-300">
              {{ tt('Colour temperature', 'Barvna temperatura') }}
              <select
                :value="cctValue"
                class="mt-1 w-full px-2 py-1 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-xs"
                data-testid="device-model-cct"
                @change="setPref('cct', Number(($event.target as HTMLSelectElement).value))">
                <option v-for="k in CCT_OPTIONS" :key="k" :value="k">
                  {{ k }} K · {{ tt(cctLabel(k).en, cctLabel(k).sl) }}
                </option>
              </select>
            </label>
            <label class="text-xs text-gray-600 dark:text-gray-300">
              {{ tt('Beam (°)', 'Snop (°)') }}
              <input
                :value="beamValue"
                type="number"
                min="8"
                max="140"
                step="2"
                :disabled="!beamApplies"
                class="mt-1 w-full px-2 py-1 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-xs disabled:opacity-60"
                data-testid="device-model-beam"
                @change="setPref('beamDeg', numberFrom($event, beamValue))" />
            </label>
          </div>
          <label class="text-xs text-gray-600 dark:text-gray-300 block">
            {{ tt('Shadow', 'Senca') }}
            <select
              :value="shadowValue"
              :disabled="!beamApplies"
              class="mt-1 w-full px-2 py-1 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-xs disabled:opacity-60"
              data-testid="device-model-shadow"
              @change="setShadowPref(($event.target as HTMLSelectElement).value as 'auto' | 'on' | 'off')">
              <option value="auto">{{ tt('Auto — fill a free slot', 'Samodejno — prosta mesta') }}</option>
              <option value="on">{{ tt('Casts a shadow', 'Meče senco') }}</option>
              <option value="off">{{ tt('No shadow', 'Brez sence') }}</option>
            </select>
          </label>
          <!--
            Lux on the floor, and keeping the light in its own room (owner, 2026-10-21:
            *"i still see no lux setting. all lights are too bright.. the globe light is
            reaching too far.. how about limiting light within room bounds (with an
            option)"*). Lux is what a lighting designer specifies; candela is what the
            engine needs, so the number is solved by inverse square over the mounting
            height.
          -->
          <div class="grid grid-cols-2 gap-2">
            <label class="text-xs text-gray-600 dark:text-gray-300">
              {{ tt('Lux on floor', 'Lux na tleh') }}
              <input
                :value="luxValue"
                type="number"
                min="5"
                max="4000"
                step="10"
                placeholder="auto"
                class="mt-1 w-full px-2 py-1 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-xs"
                data-testid="device-model-lux"
                @change="setLux(numberFrom($event, typeof luxValue === 'number' ? luxValue : 0))" />
            </label>
            <label class="text-xs text-gray-600 dark:text-gray-300 flex items-end gap-2 pb-1">
              <input
                :checked="clipToRoom"
                type="checkbox"
                class="accent-sky-500"
                data-testid="device-model-clip"
                @change="setClipToRoom(($event.target as HTMLInputElement).checked)" />
              {{ tt('Limit to room', 'Omeji na sobo') }}
            </label>
          </div>
          <!--
            Which way the cone points (owner, 2026-10-21: *"how about cone up/down/backwards
            direction for wall lights sconces"*). A wall sconce used to be welded to "into
            the room"; an uplight was impossible to model.
          -->
          <label class="text-xs text-gray-600 dark:text-gray-300 block">
            {{ tt('Cone direction', 'Smer stožca') }}
            <select
              :value="aimValue"
              :disabled="!beamApplies"
              class="mt-1 w-full px-2 py-1 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-xs disabled:opacity-60"
              data-testid="device-model-aim"
              @change="setAim(($event.target as HTMLSelectElement).value as 'auto' | 'down' | 'up' | 'out' | 'back')">
              <option value="auto">{{ tt('Auto (into the room / down)', 'Samodejno (v sobo / navzdol)') }}</option>
              <option value="down">{{ tt('Down — step light', 'Navzdol — stopnična') }}</option>
              <option value="up">{{ tt('Up — uplight', 'Navzgor — navzgor') }}</option>
              <option value="out">{{ tt('Into the room', 'V sobo') }}</option>
              <option value="back">{{ tt('Toward the wall', 'Proti steni') }}</option>
            </select>
          </label>
          <p class="text-[11px] text-gray-500 dark:text-gray-400" data-testid="device-model-lux-hint">
            {{
              tt(
                'Empty = the fixture’s own brightness. A number is the light you want on the floor, in lux — 100 reads as a living room, 300 as a work surface. “Limit to room” keeps it inside this room’s own bounds.',
                'Prazno = lastna svetilnost svetila. Število je svetloba, ki jo želite na tleh, v luksih — 100 je dnevna soba, 300 delovna površina. »Omeji na sobo« jo zadrži znotraj meja te sobe.',
              )
            }}
          </p>
          <p
            class="text-[11px] text-gray-500 dark:text-gray-400"
            data-testid="device-model-shadow-hint"
            v-if="beamApplies">
            {{
              tt(
                'Only a handful of fixtures can cast at once — a GPU has about sixteen texture units, and the model needs some. Pick the ones that matter.',
                'Hkrati lahko senco meče le nekaj svetil — GPE ima približno šestnajst teksturnih enot, del jih potrebuje model. Izberite tiste, ki so pomembne.',
              )
            }}
          </p>
          <p v-if="!beamApplies" class="text-[11px] text-amber-500" data-testid="device-model-beam-hint">
            {{
              tt(
                'A globe or line light is a POINT light: it has no cone, so beam and direction do not apply, and it casts no shadow — its light is not stopped by walls. Use a spot fixture where it must be aimed or must cast.',
                'Kroglasta ali linijska svetloba je TOČKOVNO svetilo: nima stožca, zato snop in smer ne veljata, in ne meče sence — svetloba se ne ustavi ob stenah. Za usmerjanje ali senco uporabite reflektor.',
              )
            }}
          </p>

          <div class="grid grid-cols-2 gap-2">
            <label v-for="field in sizeFields" :key="field.key" class="text-xs text-gray-600 dark:text-gray-300">
              {{ tt(field.en, field.sl) }}
              <input
                :value="sizeMm(field.key)"
                type="number"
                :min="field.min"
                :max="field.max"
                :step="field.step"
                class="mt-1 w-full px-2 py-1 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-xs"
                :data-testid="`device-model-size-${field.key}`"
                @change="setSize(field.key, numberFrom($event, sizeMm(field.key)))" />
            </label>
            <label class="text-xs text-gray-600 dark:text-gray-300">
              {{ tt('Turn (°)', 'Obrat (°)') }}
              <input
                :value="placement.yawDeg"
                type="number"
                min="-360"
                max="360"
                step="15"
                class="mt-1 w-full px-2 py-1 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-xs"
                data-testid="device-model-yaw"
                @change="setPlacement('yawDeg', numberFrom($event, placement.yawDeg))" />
            </label>
          </div>

          <div>
            <label class="block text-xs font-medium text-gray-600 dark:text-gray-300 mb-1">{{
              tt('Accent', 'Poudarek')
            }}</label>
            <div class="flex flex-wrap gap-1.5" data-testid="device-model-accents">
              <button
                v-for="option in accentOptions"
                :key="option.key"
                type="button"
                class="px-2 py-1 rounded-lg border text-xs transition-colors"
                :class="
                  prefs.accent === option.key
                    ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300'
                    : 'border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 hover:border-blue-400'
                "
                :data-testid="`device-model-accent-${option.key}`"
                @click="setAccent(option.key)">
                <span
                  class="inline-block w-2.5 h-2.5 rounded-full mr-1 align-middle"
                  :style="{ backgroundColor: cssOf(option.rgb) }" />
                {{ tt(option.en, option.sl) }}
              </button>
            </div>
          </div>
        </template>

        <!-- Water tanks: the cistern's SHAPE. Its level is LIVE (the device's own
             `level_percentage`), so it is not a field here — the water line in
             the view is the value, and a number typed here would be a second
             opinion the hub owns. -->
        <template v-else-if="familyType === 'water-tank' || familyType === 'waste-tank'">
          <div class="grid grid-cols-1 gap-2">
            <label v-if="familyType === 'water-tank'" class="text-xs text-gray-600 dark:text-gray-300">
              {{ tt('Cistern shape', 'Oblika cisterne') }}
              <select
                :value="prefs.shape ?? 'ibc'"
                class="mt-1 w-full px-2 py-1 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-xs"
                data-testid="device-model-tank-shape"
                @change="setTankShape($event)">
                <option value="ibc">{{ tt('IBC 1000 l (tote)', 'IBC 1000 l (kontejner)') }}</option>
                <option value="oval-vertical">{{ tt('Oval, standing', 'Ovalna, stoječa') }}</option>
                <option value="oval-horizontal">{{ tt('Oval, lying', 'Ovalna, ležeča') }}</option>
              </select>
            </label>
            <label class="text-xs text-gray-600 dark:text-gray-300">
              {{ tt('Turn (°)', 'Obrat (°)') }}
              <input
                :value="placement.yawDeg"
                type="number"
                min="-360"
                max="360"
                step="15"
                class="mt-1 w-full px-2 py-1 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-xs"
                data-testid="device-model-yaw"
                @change="setPlacement('yawDeg', numberFrom($event, placement.yawDeg))" />
            </label>
          </div>
          <span class="text-[11px] text-gray-400">{{
            tt(
              'The tank stands on its pin: the pin is the centre of its footprint on the floor. The water follows the device’s level reading.',
              'Cisterna stoji na žebljičku: žebljiček je sredina tlorisa na tleh. Voda sledi odčitku nivoja naprave.',
            )
          }}</span>
        </template>

        <!-- Shades: the frame's own size and the family's shape params. -->
        <template v-else>
          <div class="grid grid-cols-2 gap-2">
            <template v-if="familyType === 'venetian-blind'">
              <label class="text-xs text-gray-600 dark:text-gray-300">
                {{ tt('Slat pitch (mm)', 'Korak lamel (mm)') }}
                <input
                  :value="prefs.slatPitchMm ?? 78"
                  type="number"
                  min="40"
                  max="120"
                  step="2"
                  class="mt-1 w-full px-2 py-1 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-xs"
                  data-testid="device-model-slat-pitch"
                  @change="setPref('slatPitchMm', numberFrom($event, prefs.slatPitchMm ?? 78))" />
              </label>
            </template>
            <template v-else-if="familyType === 'garage-door'">
              <label class="text-xs text-gray-600 dark:text-gray-300">
                {{ tt('Sections', 'Sekcije') }}
                <input
                  :value="prefs.rows ?? 5"
                  type="number"
                  min="3"
                  max="8"
                  step="1"
                  class="mt-1 w-full px-2 py-1 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-xs"
                  data-testid="device-model-rows"
                  @change="setPref('rows', numberFrom($event, prefs.rows ?? 5))" />
              </label>
              <label class="text-xs text-gray-600 dark:text-gray-300">
                {{ tt('Panels per section', 'Paneli na sekcijo') }}
                <input
                  :value="prefs.cols ?? 8"
                  type="number"
                  min="2"
                  max="14"
                  step="1"
                  class="mt-1 w-full px-2 py-1 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-xs"
                  data-testid="device-model-cols"
                  @change="setPref('cols', numberFrom($event, prefs.cols ?? 8))" />
              </label>
            </template>
            <template v-else-if="familyType === 'sliding-gate'">
              <label class="text-xs text-gray-600 dark:text-gray-300">
                {{ tt('Sections', 'Sekcije') }}
                <input
                  :value="prefs.sections ?? 2"
                  type="number"
                  min="2"
                  max="3"
                  step="1"
                  class="mt-1 w-full px-2 py-1 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-xs"
                  data-testid="device-model-sections"
                  @change="setPref('sections', numberFrom($event, prefs.sections ?? 2))" />
              </label>
            </template>
            <template v-else-if="familyType === 'swing-gate'">
              <label class="text-xs text-gray-600 dark:text-gray-300">
                {{ tt('Leaves', 'Krila') }}
                <input
                  :value="prefs.leaves ?? 2"
                  type="number"
                  min="1"
                  max="2"
                  step="1"
                  class="mt-1 w-full px-2 py-1 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-xs"
                  data-testid="device-model-leaves"
                  @change="setPref('leaves', numberFrom($event, prefs.leaves ?? 2))" />
              </label>
            </template>
            <template v-else>
              <label class="text-xs text-gray-600 dark:text-gray-300">
                {{ tt('Openness (%)', 'Prehodnost (%)') }}
                <input
                  :value="prefs.opennessPct ?? 3"
                  type="number"
                  min="0"
                  max="30"
                  step="1"
                  class="mt-1 w-full px-2 py-1 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-xs"
                  data-testid="device-model-openness"
                  @change="setPref('opennessPct', numberFrom($event, prefs.opennessPct ?? 3))" />
              </label>
            </template>
            <label class="text-xs text-gray-600 dark:text-gray-300">
              {{ tt('Turn (°)', 'Obrat (°)') }}
              <input
                :value="placement.yawDeg"
                type="number"
                min="-360"
                max="360"
                step="15"
                class="mt-1 w-full px-2 py-1 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-xs"
                data-testid="device-model-yaw"
                @change="setPlacement('yawDeg', numberFrom($event, placement.yawDeg))" />
            </label>
            <label class="text-xs text-gray-600 dark:text-gray-300">
              {{ tt('Slope (°)', 'Nagib (°)') }}
              <input
                :value="placement.pitchDeg"
                type="number"
                min="-90"
                max="90"
                step="5"
                class="mt-1 w-full px-2 py-1 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-xs"
                data-testid="device-model-pitch"
                @change="setPlacement('pitchDeg', numberFrom($event, placement.pitchDeg))" />
            </label>
          </div>
          <span class="text-[11px] text-gray-400">{{
            tt(
              'Measured from the window’s own plane: 0° is flat in the window, and the element’s own normal decides which way it faces.',
              'Merjeno od lastne ravnine okna: 0° pomeni poravnano z oknom, smer pa določi lastna normala elementa.',
            )
          }}</span>

          <div class="grid grid-cols-2 gap-2">
            <label class="text-xs text-gray-600 dark:text-gray-300">
              {{ tt('Width (mm)', 'Širina (mm)') }}
              <input
                :value="placement.widthMm"
                type="number"
                min="300"
                max="6000"
                step="50"
                class="mt-1 w-full px-2 py-1 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-xs"
                data-testid="device-model-width"
                @change="setPlacement('widthMm', numberFrom($event, placement.widthMm))" />
            </label>
            <label class="text-xs text-gray-600 dark:text-gray-300">
              {{ isAccessFamily ? tt('Height (mm)', 'Višina (mm)') : tt('Drop (mm)', 'Spust (mm)') }}
              <input
                :value="placement.dropFullMm"
                type="number"
                min="300"
                max="6000"
                step="50"
                class="mt-1 w-full px-2 py-1 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-xs"
                data-testid="device-model-drop"
                @change="setPlacement('dropFullMm', numberFrom($event, placement.dropFullMm))" />
            </label>
          </div>

          <label v-if="!isAccessFamily" class="flex items-center gap-2 cursor-pointer">
            <input
              :checked="prefs.cassetteHidden"
              type="checkbox"
              class="w-4 h-4 text-blue-600 border-gray-300 rounded"
              data-testid="device-model-cassette"
              @change="setPref('cassetteHidden', checkedFrom($event))" />
            <span class="text-xs font-medium text-gray-700 dark:text-gray-300">{{
              tt('Hidden cassette (flush head box)', 'Skrita kaseta (poravnan nosilec)')
            }}</span>
          </label>
        </template>
      </template>
    </div>

    <!--
      A model imported before this panel dropped its IFC upload (the owner's
      "remove import"): the file stays attached and removable, with its scale,
      but nothing new can be imported here — the grid above is the only way in.
    -->
    <template v-if="source === 'ifc'">
      <div v-if="ifc" class="flex items-center gap-2 px-2 py-1.5 rounded-lg bg-gray-100 dark:bg-gray-700">
        <span class="material-symbols-outlined text-[18px] text-blue-500">deployed_code</span>
        <div class="flex-1 min-w-0">
          <p class="text-xs text-gray-800 dark:text-gray-100 truncate" data-testid="device-model-ifc-name">
            {{ ifc.name }}
          </p>
          <p class="text-[10px] text-gray-500 dark:text-gray-400">{{ formatBytes(ifc.bytes) }} · {{ ifc.url }}</p>
        </div>
        <button
          type="button"
          class="text-red-500 hover:text-red-600 text-[11px] shrink-0"
          data-testid="device-model-ifc-remove"
          @click="removeIfc">
          {{ tt('Remove', 'Odstrani') }}
        </button>
      </div>

      <p class="text-[11px] text-gray-400">
        {{
          tt(
            'Imported earlier — remove it to go back to a library model.',
            'Uvoženo prej — odstranite za vrnitev na model iz knjižnice.',
          )
        }}
      </p>

      <label v-if="ifc" class="block text-xs text-gray-600 dark:text-gray-300">
        {{ tt('Scale', 'Merilo') }}
        <input
          :value="ifc.scale"
          type="number"
          min="0.001"
          max="1000"
          step="0.5"
          class="mt-1 w-full px-2 py-1 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-xs"
          data-testid="device-model-ifc-scale"
          @change="setIfcScale(numberFrom($event, ifc?.scale ?? 1))" />
        <span class="text-[11px] text-gray-400">{{
          tt('Units are read from the file’s own size.', 'Enote se preberejo iz velikosti datoteke.')
        }}</span>
      </label>
    </template>

    <p v-if="error" class="text-[11px] text-red-500" data-testid="device-model-error">{{ error }}</p>
  </div>
</template>

<script setup lang="ts">
/**
 * DeviceModel3dConfig — a device's own 3D model, edited on the element that
 * already carries the device, card t_ecc4a6e8.
 *
 * Three sources, one slot: nothing / a parametric family from the library —
 * the SHADE families (venetian blind, zip roller, roof shutter) for a cover, the
 * LUMINAIRE family for a light (card t_d267b30d: the owner's *"i have a lamp
 * selested and it gives me shades model here"*) — / an imported IFC file
 * uploaded for THIS device. The list a device sees is its CLASS's list
 * (`libraryFamilies`), so a lamp is never offered a blind again.
 *
 * The slot lives on the DEVICE (`settings.model3d`, card t_6b2c5f01) and a
 * widget's own config still carries its older copy as the back-compat fallback —
 * the same stored shape, one parser (`utils/bimDeviceModel.ts`). It survives a
 * reload with whatever carries it and never needs a second widget to be added.
 *
 * ONE surface (owner, 2026-10-30): the slot is offered in the device's own editor
 * alone. The device-icon widget's control settings used to show this same section,
 * which asked the device's own setting twice. The host owns persistence: this
 * section only reads the bag it is handed and emits the edited slot.
 */
import { computed, onMounted, ref, watch } from 'vue';

import { apiFetch, apiFetchRaw } from '@/services/api';
import { tt } from '@/utils/consoleText';
import { useDashboardStore } from '@/stores/dashboard';
import { useEntitiesStore } from '@/stores/entities';
import { isLightDevice } from '@/stores/lights';
import { isShadeDevice } from '@/utils/shadeDevices';
import {
  LUMINAIRE_SIZE_FIELDS,
  RAL_TONES,
  defaultLibraryPrefs,
  defaultLibraryType,
  defaultPlacement,
  familyKind,
  formatBytes,
  libraryFamilies,
  libraryFamily,
  parseDeviceModel,
  readLuminaireSize,
  widgetAnchor,
  type DeviceModel3d,
  type DeviceModelKind,
  type DeviceModelSource,
  type LibraryPrefs,
  type LibraryModelType,
  type RalTone,
  type Vec3,
} from '@/utils/bimDeviceModel';
import {
  LUMINAIRE_ACCENT_RGB,
  LUMINAIRE_ACCENTS,
  LUMINAIRE_VARIANTS,
  isLuminaireAccent,
  isLuminaireVariant,
  luminaireVariantFor,
  type LuminaireAccent,
  type LuminaireVariant,
} from '@/utils/bimLuminaireSpec';
import { LUMINAIRE_ACCENT_TITLES, LUMINAIRE_VARIANT_TITLES, luminaireSizeMm } from '@/utils/bimLuminaireParts';
import { beamDegFor, CCT_PRESETS, clampCct } from '@/utils/bimFixtureLights';
import {
  LUMINAIRE_MOUNTS,
  LUMINAIRE_MOUNT_TITLES,
  isLuminaireMount,
  mountDefaultVariant,
  mountForVariant,
  mountPlacement,
  variantFitsMount,
  type LuminaireMount,
  type MountPlacement,
  type MountRoom,
} from '@/utils/bimLuminaireMount';
import { LUMINAIRE_DEFAULT_SIZE_MM } from '@/utils/bimLuminaireSpec';
import { parseBimRoomsPayload, type BimRoom } from '@/utils/bimRooms';
import { dashboardBimModels } from '@/utils/bimDashboardModels';
import { readNumberField } from '@/utils/numberField';
import { isAccessDevice } from '@/utils/accessDevices';
import { isWasteTankDevice, isWaterTankDevice } from '@/utils/waterTankDevices';
import { waterTankShape } from '@/utils/bimWaterTankParts';

interface Props {
  /**
   * The bag the stored slot is read from: the device's own settings. Only the
   * slot key is read from it.
   */
  config: unknown;
  /** The device the model belongs to — the upload is filed against it. */
  deviceId: string;
  /**
   * The BIM rooms document's rooms, when the host ALREADY holds them (the BIM
   * page's own panels do). The section resolves the device's room from this and
   * reads nothing. Shown on its own — the device editor — it reads the ACTIVE
   * dashboard's document instead, never a new kind of data (card t_0b96786f).
   */
  rooms?: readonly BimRoom[];
  /**
   * Whether the section draws its own title. The device editor folds this section
   * into a one-line row whose summary already says "3D model", so the section
   * drops its heading there.
   */
  heading?: 'show' | 'hide';
}

const props = withDefaults(defineProps<Props>(), { heading: 'show' });
const emit = defineEmits<{
  'update:model': [value: DeviceModel3d | null];
}>();

/** The hub's devices — the section reads the one it belongs to (its class). */
const entitiesStore = useEntitiesStore();

const ralTones = RAL_TONES;
const luminaireVariants = LUMINAIRE_VARIANTS;
const variantTitles = LUMINAIRE_VARIANT_TITLES;

const error = ref('');

/** The slot being edited, seeded from the widget's own stored one. */
const model = ref<DeviceModel3d | null>(parseDeviceModel(props.config));

watch(
  () => props.config,
  (config) => {
    const next = parseDeviceModel(config);
    // Keep the edit in progress: only re-seed when the stored slot really differs.
    if (JSON.stringify(next) !== JSON.stringify(model.value)) model.value = next;
  },
);

watch(model, (value) => emit('update:model', value), { deep: true });

const anchor = computed<Vec3 | null>(() => widgetAnchor(props.config));
const source = computed<DeviceModelSource | 'none'>(() => model.value?.source ?? 'none');
const familyType = computed<LibraryModelType | null>(() => model.value?.library?.type ?? null);
const ifc = computed(() => model.value?.ifc ?? null);

/**
 * The DEVICE the model belongs to, read off the entities store so it tracks the
 * hub (the store follows `device:updated`, and the widget config is handed the
 * device's id on both surfaces).
 *
 * This is what makes the library CLASS-AWARE, which is the report this section
 * answers (owner, 2026-10-11: *"i have a lamp selested and it gives me shades
 * model here"*): the family list below is the family list of THIS device's
 * class, never a shade list for a lamp.
 */
const linkedDevice = computed(() => (props.deviceId ? entitiesStore.getDevice(props.deviceId) : undefined));

/**
 * The class the device's own words name. The two matchers are the CANONICAL
 * ones — `stores/lights.isLightDevice` (the matcher the live luminaire layer
 * lists its lights with, `useBimLiveLights.ts`) and
 * `utils/shadeDevices.isShadeDevice` (the shade surfaces') — so this is not a
 * third hand-kept word list that can drift from them.
 */
const wordsKind = computed<DeviceModelKind>(() => {
  const device = linkedDevice.value;
  if (!device) return 'unknown';
  if (isLightDevice(device)) return 'light';
  // A WATER TANK is its own class (the hub's `water_tank`, controlType
  // `sensor`) — it must be offered its cistern and nothing else. The WASTE
  // tank is checked first: its class (`waste_tank`) contains the word `tank`.
  if (isWasteTankDevice(device)) return 'waste';
  if (isWaterTankDevice(device)) return 'tank';
  // ACCESS before the covers: a hub spells a garage door's control `cover` in
  // the older mapping shape (`deviceControl`), and the door was therefore being
  // offered the shade families — the report this section's own class rule
  // answers, mirrored (owner, 2026-10-16: *"add garage door to library!"*).
  if (isAccessDevice(device)) return 'access';
  if (isShadeDevice(device)) return 'cover';
  return 'unknown';
});

/** The class the STORED slot already belongs to — what an attach made before a re-class keeps. */
const slotKind = computed<DeviceModelKind>(() => (familyType.value ? familyKind(familyType.value) : 'unknown'));

const deviceKind = computed<DeviceModelKind>(() => (wordsKind.value === 'unknown' ? slotKind.value : wordsKind.value));

/** The families this device is offered — grouped by its class, one rule (`libraryFamilies`). */
const library = computed(() => libraryFamilies(deviceKind.value));

const libraryGroupLabel = computed(() => {
  if (deviceKind.value === 'light') return tt('Light fixtures', 'Svetila');
  if (deviceKind.value === 'cover') return tt('Covers and shades', 'Senčila');
  if (deviceKind.value === 'access') return tt('Doors and gates', 'Vrata in prehodi');
  if (deviceKind.value === 'tank') return tt('Water tanks', 'Vodni zbiralniki');
  if (deviceKind.value === 'waste') return tt('Waste water', 'Odpadna voda');
  return tt('All families', 'Vse družine');
});

/**
 * The variant a FRESH attach starts from: the fixture the device's own name
 * classifies to (the pipeline's `variant_for`: a "Kitchen Island Pendant" opens
 * as a pendant, not as a spot) — the same fixture the house itself would build
 * for that lamp. The operator's own pick is stored in the prefs from then on.
 */
const seedVariant = computed<LuminaireVariant | undefined>(() =>
  deviceKind.value === 'light' ? luminaireVariantFor(linkedDevice.value?.deviceName ?? '') : undefined,
);

/** The access class (a garage door, a sliding gate, a swing gate) — the family
 *  group whose frame is an OPENING and whose own params are counted, not tuned. */
const isAccessFamily = computed(() => (familyType.value ? familyKind(familyType.value) === 'access' : false));

const prefs = computed(
  () =>
    model.value?.library?.prefs ??
    defaultLibraryPrefs(familyType.value ?? defaultLibraryType(deviceKind.value), { variant: seedVariant.value }),
);

/** True while the attached family is the luminaire — its params are its own. */
const isLuminaire = computed(() => familyType.value === 'luminaire');
const luminaireVariant = computed<LuminaireVariant>(() => prefs.value.variant ?? 'spot');
const sizeFields = computed(() => LUMINAIRE_SIZE_FIELDS[luminaireVariant.value]);
const accentOptions = computed(() =>
  LUMINAIRE_ACCENTS.map((key) => ({
    key,
    rgb: LUMINAIRE_ACCENT_RGB[key],
    ...LUMINAIRE_ACCENT_TITLES[key],
  })),
);

/** One size field's value in mm — the module's DEFAULTS unless overridden. */
function sizeMm(key: string): number {
  const size = luminaireSizeMm(luminaireVariant.value, prefs.value.sizeMm ?? {});
  return size[key] ?? 0;
}

// ─── the mounting spot (card t_0b96786f) ────────────────────────────────────

/** The five spots, in the owner's order, with both languages (the module's table). */
const mountOptions = LUMINAIRE_MOUNTS.map((value) => ({ value, ...LUMINAIRE_MOUNT_TITLES[value] }));

const dashboards = useDashboardStore();

/** The rooms document, when this section had to read it (a host's `rooms` wins). */
const roomsDocument = ref<readonly BimRoom[] | null>(null);
const mountRoomsLoading = ref(false);

/** The hub room the device belongs to — the link the rooms document is keyed by. */
const mountRoomId = computed<string | null>(() => linkedDevice.value?.roomId ?? null);

/**
 * The room's own box, or `null` when the device is in no room. A room with
 * several sections seats on the FIRST one — the same rule the Devices tab's
 * auto-populate states (`BimRoom.sections[0]`); the operator drags afterwards.
 */
const mountRoom = computed<MountRoom | null>(() => {
  const roomId = mountRoomId.value;
  if (!roomId) return null;
  const rooms = props.rooms ?? roomsDocument.value;
  if (!rooms) return null;
  const room = rooms.find((entry) => entry.roomId === roomId) ?? null;
  const section = room?.sections[0];
  if (!section) return null;
  return { center: section.center, size: section.size, yawDeg: section.yawDeg, y0: section.y0, y1: section.y1 };
});

/** The mount the slot is on: its stored word, else the variant's own seat. */
const mount = computed<LuminaireMount>(() => prefs.value.mount ?? mountForVariant(luminaireVariant.value));

/**
 * The LIGHT's own pair (tier 1, owner 2026-10-18: *"we'll go with tier 1"*): the
 * colour temperature it burns at and the cone it throws. Both are design
 * decisions on the fixture, stored beside its size — the glow and the real light
 * read the same numbers, so what the panel says is what the room gets.
 */
const CCT_OPTIONS = CCT_PRESETS;
const cctValue = computed(() => clampCct(prefs.value.cct));
const beamValue = computed(() => beamDegFor(luminaireVariant.value, prefs.value.beamDeg));

/**
 * May this fixture cast a shadow (owner, 2026-10-21: *"per lamp setting which gets the
 * real shadow and which not"*). Only a dozen can at once, so WHICH dozen is a design
 * decision — `auto` means "fill whatever slots the explicit choices leave".
 */
/**
 * The target illuminance on the floor, lux. Empty means "the fixture's own table" — the
 * control must be able to say "no opinion", or every lamp becomes a number nobody chose.
 */
const luxValue = computed<number | ''>(() => {
  const stored = prefs.value.lux;
  return typeof stored === 'number' ? stored : '';
});

function setLux(value: number): void {
  const current = model.value;
  if (!current?.library) return;
  const next = { ...current.library.prefs };
  if (!Number.isFinite(value) || value <= 0) delete next.lux;
  else next.lux = Math.round(Math.min(4000, Math.max(5, value)));
  if (JSON.stringify(next) === JSON.stringify(current.library.prefs)) return;
  model.value = { ...current, library: { ...current.library, prefs: next } };
}

/** Keep this fixture's light inside its own room — on unless explicitly turned off. */
const clipToRoom = computed(() => prefs.value.clipToRoom !== false);

function setClipToRoom(on: boolean): void {
  const current = model.value;
  if (!current?.library) return;
  const next = { ...current.library.prefs, clipToRoom: on };
  if (JSON.stringify(next) === JSON.stringify(current.library.prefs)) return;
  model.value = { ...current, library: { ...current.library, prefs: next } };
}

/**
 * Which way this fixture's cone points. `auto` is the old rule — into the room for a wall
 * fixture, down for everything else — and is stored as the ABSENCE of the pref.
 */
const aimValue = computed<'auto' | 'down' | 'up' | 'out' | 'back'>(() => prefs.value.aim ?? 'auto');

function setAim(value: 'auto' | 'down' | 'up' | 'out' | 'back'): void {
  const current = model.value;
  if (!current?.library) return;
  const next = { ...current.library.prefs };
  if (value === 'auto') delete next.aim;
  else next.aim = value;
  if (JSON.stringify(next) === JSON.stringify(current.library.prefs)) return;
  model.value = { ...current, library: { ...current.library, prefs: next } };
}

const shadowValue = computed<'auto' | 'on' | 'off'>(() => {
  const stored = prefs.value.shadow;
  return stored === true ? 'on' : stored === false ? 'off' : 'auto';
});

function setShadowPref(value: 'auto' | 'on' | 'off'): void {
  const current = model.value;
  if (!current?.library) return;
  const prefs = { ...current.library.prefs };
  if (value === 'auto') delete prefs.shadow;
  else prefs.shadow = value === 'on';
  if (JSON.stringify(prefs) === JSON.stringify(current.library.prefs)) return;
  model.value = { ...current, library: { ...current.library, prefs } };
}
/** A globe and a line carry no cone — the beam field is drawn disabled, with the reason. */
const beamApplies = computed(() => beamValue.value > 0);

/** A word beside the Kelvin, so the number is a colour and not a code. */
function cctLabel(k: number): { en: string; sl: string } {
  if (k <= 2400) return { en: 'candle', sl: 'sveča' };
  if (k <= 2900) return { en: 'warm', sl: 'topla' };
  if (k <= 3600) return { en: 'warm white', sl: 'toplo bela' };
  if (k <= 4500) return { en: 'neutral', sl: 'nevtralna' };
  return { en: 'cool', sl: 'hladna' };
}

/**
 * One row of the library list. A cover/access family is ONE row; the luminaire
 * family is one row PER VARIANT — the fixtures are what the operator chooses
 * between, and a single "luminaire" row read as "only the current lamp"
 * (owner, 2026-10-18: *"i dont see list of light fixtures here.. only current
 * assigned lamp"*). One list, one click target: a light picks its fixture the
 * same way a shade picks its family.
 */
interface LibraryRow {
  key: string;
  testid: string;
  type: LibraryModelType;
  variant?: LuminaireVariant;
  icon: string;
  titleEn: string;
  titleSl: string;
  modelLabel: string;
}

/**
 * The symbol each luminaire fixture shows in its tile. The tiles are read as
 * pictures, so a pendant and a bollard must not carry the same bulb glyph.
 * Anything the map does not name falls back to the family's own icon.
 */
const LUMINAIRE_VARIANT_ICONS: Record<LuminaireVariant, string> = {
  spot: 'highlight',
  pendant: 'dine_lamp',
  wall: 'wall_lamp',
  pole: 'light',
  strip: 'lightstrip',
  bulb: 'lightbulb',
  panel: 'crop_square',
};

const libraryRows = computed<LibraryRow[]>(() => {
  const rows: LibraryRow[] = [];
  for (const family of library.value) {
    if (family.type !== 'luminaire') {
      rows.push({
        key: family.type,
        testid: `device-model-family-${family.type}`,
        type: family.type,
        icon: family.icon,
        titleEn: family.titleEn,
        titleSl: family.titleSl,
        modelLabel: family.model,
      });
      continue;
    }
    // A lamp whose fixture the DEVICE owns is not offered the fixture as a model:
    // the parametric body comes from its light section, so the only mesh question
    // left here is "an imported file, or none".
    for (const variant of LUMINAIRE_VARIANTS) {      const title = LUMINAIRE_VARIANT_TITLES[variant];
      rows.push({
        key: `luminaire:${variant}`,
        testid: `device-model-family-luminaire-${variant}`,
        type: 'luminaire',
        variant,
        icon: LUMINAIRE_VARIANT_ICONS[variant] ?? family.icon,
        titleEn: title.en,
        titleSl: title.sl,
        modelLabel: `${family.model} · ${variant}`,
      });
    }
  }
  return rows;
});

/** A row is the attached one when its family — and, for a luminaire, its variant — matches. */
function rowActive(row: LibraryRow): boolean {
  if (familyType.value !== row.type) return false;
  if (!row.variant) return true;
  return luminaireVariant.value === row.variant;
}

/** Attach the family the row names (a luminaire row names its variant too). */
function pickRow(row: LibraryRow): void {
  pickFamily(row.type, row.variant);
}

/** The drop a hanging fixture would use, mm — its own size first (what renders). */
function dropMmForMount(): number {
  const sizeDrop = prefs.value.sizeMm?.drop;
  if (typeof sizeDrop === 'number' && Number.isFinite(sizeDrop) && sizeDrop > 0) return sizeDrop;
  const legacy = prefs.value.dropFullMm;
  if (typeof legacy === 'number' && Number.isFinite(legacy) && legacy > 0) return legacy;
  return LUMINAIRE_DEFAULT_SIZE_MM.pendant.drop;
}

/** A standing fixture's own height, mm — its origin is the TOP of its head. */
function poleHeightMm(): number {
  const sizeHeight = prefs.value.sizeMm?.h;
  if (typeof sizeHeight === 'number' && Number.isFinite(sizeHeight) && sizeHeight > 0) return sizeHeight;
  return LUMINAIRE_DEFAULT_SIZE_MM.pole.h;
}

/** Where the mount puts the fixture right now — the consequence shown beside the select. */
const mountPlacementOf = computed<MountPlacement | null>(() => {
  const room = mountRoom.value;
  if (!room) return null;
  return mountPlacement({
    room,
    mount: mount.value,
    prefs: { dropFullMm: dropMmForMount() },
    fixtureHeightM: poleHeightMm() / 1000,
  });
});

/** `2.66 m · Ceiling` — the height the operator is about to save, before saving it. */
const mountSummary = computed(() => {
  const title = LUMINAIRE_MOUNT_TITLES[mount.value];
  const label = tt(title.en, title.sl);
  const placement = mountPlacementOf.value;
  return placement ? `${placement.y.toFixed(2)} m · ${label}` : '—';
});

/**
 * The rooms document, for the surfaces that were handed none.
 *
 * The mount needs the room's absolute floor/ceiling, and that lives in the
 * per-model rooms document (`BimRoom.sections[].y0/y1`) — the one the BIM page
 * reads. A host that holds it hands it down through `rooms` and this never
 * runs. A section shown on its own reads the ACTIVE dashboard's document through
 * the same two routes the layout uses (`/bim-models` → the served row, then its
 * `/rooms`), and a dashboard with no served model simply has no room to seat a
 * fixture in — never an error the operator has to clear.
 */
async function loadMountRooms(): Promise<void> {
  if (props.rooms) return;
  const dashboardId = String(dashboards.activeDashboardId ?? '');
  if (!dashboardId) {
    roomsDocument.value = null;
    return;
  }
  mountRoomsLoading.value = true;
  try {
    const rows = await apiFetch<unknown>(`/dashboards/${dashboardId}/bim-models`);
    const active = dashboardBimModels(rows).find((row) => row.active) ?? null;
    if (!active) {
      roomsDocument.value = null;
      return;
    }
    const payload = await apiFetch<unknown>(`/dashboards/${dashboardId}/bim-models/${active.id}/rooms`);
    roomsDocument.value = parseBimRoomsPayload(payload, active.id).rooms;
  } catch {
    roomsDocument.value = null;
  } finally {
    mountRoomsLoading.value = false;
  }
}

onMounted(() => {
  void loadMountRooms();
});

watch([() => dashboards.activeDashboardId, mountRoomId], () => {
  void loadMountRooms();
});

/**
 * Re-seat the fixture on another mount: one edit, one save.
 *
 * The mount writes `prefs.mount`, keeps the operator's fixture when it can be
 * seated this way (else takes the mount's own default, D5), and moves the slot
 * to the point `mountPlacement()` names — origin, facing. A pendant is stored at
 * the drop the room allows, so the drawn shade cannot hang through the slab.
 */
async function setMount(value: string): Promise<void> {
  const current = model.value;
  if (!current?.library || !isLuminaireMount(value)) return;
  const room = mountRoom.value;
  if (!room) return;
  const currentVariant = current.library.prefs.variant ?? 'spot';
  const variant = variantFitsMount(currentVariant, value) ? currentVariant : mountDefaultVariant(value);
  const sizeMm = variant === currentVariant ? current.library.prefs.sizeMm : undefined;
  const placement = mountPlacement({
    room,
    mount: value,
    prefs: { dropFullMm: sizeMm?.drop ?? dropMmForMount() },
    fixtureHeightM: (sizeMm?.h ?? poleHeightMm()) / 1000,
  });
  const nextPrefs: LibraryPrefs = { ...current.library.prefs, variant, mount: value };
  if (variant !== currentVariant) nextPrefs.sizeMm = undefined;
  if (value === 'pendant' && placement.dropM !== undefined) {
    nextPrefs.sizeMm = { ...(nextPrefs.sizeMm ?? {}), drop: Math.round(placement.dropM * 1000) };
  }
  model.value = {
    ...current,
    library: { ...current.library, prefs: nextPrefs },
    placement: {
      ...current.placement,
      origin: [placement.x, placement.y, placement.z],
      yawDeg: placement.yawDeg,
      pitchDeg: 0,
    },
  };
}

const placement = computed(() => model.value?.placement ?? defaultPlacement(anchor.value ?? [0, 0, 0]));
const activeTone = computed<RalTone | null>(() => ralTones.find((tone) => tone.code === prefs.value.color) ?? null);
const colourLabel = computed(() =>
  isLuminaire.value ? tt('Body colour (RAL)', 'Barva telesa (RAL)') : tt('Colour (RAL)', 'Barva (RAL)'),
);

function cssOf(rgb: Vec3): string {
  const channel = (value: number) => Math.round(Math.min(1, Math.max(0, value)) * 255);
  return `rgb(${channel(rgb[0])}, ${channel(rgb[1])}, ${channel(rgb[2])})`;
}

/**
 * The number a field of this panel now holds (card t_8842b1fe).
 *
 * The owner: *"i edited widget and set it's width to 0, by accident.. i deleted
 * all text in field. and that when it broke"* — `Number('')` is 0, so an emptied
 * box wrote 0 mm into a blind's width (and 0 into anything else it was pointed
 * at). An empty (or unparseable) field now keeps the value it already held and a
 * typed value is clamped into the range the field itself declares — the same
 * numbers the parser clamps to on read. The box is then put back to the value
 * that STANDS, so the panel and the model never disagree.
 */
function numberFrom(event: Event, current: number): number {
  const input = event.target as HTMLInputElement;
  const next = readNumberField(input.value, current, { min: attrNumber(input.min), max: attrNumber(input.max) });
  input.value = String(next);
  return next;
}

/** A `min`/`max` attribute as a number — absent or junk means "no bound". */
function attrNumber(raw: string): number | undefined {
  const text = raw.trim();
  if (text === '') return undefined;
  const value = Number(text);
  return Number.isFinite(value) ? value : undefined;
}

function checkedFrom(event: Event): boolean {
  return (event.target as HTMLInputElement).checked;
}

/** Switching source keeps the placement (the pin) and swaps the geometry. */
function pickSource(next: DeviceModelSource | 'none'): void {
  error.value = '';
  if (next === 'none') {
    // No model: the pin keeps its icon and the view returns to the house.
    model.value = null;
    return;
  }
  if (next === 'library') {
    // The class's own default family — a LIGHT opens on the luminaire, never on
    // a blind (card t_d267b30d). The family's own seeds ride along: a light's
    // variant is the one its device name classifies to.
    const type = model.value?.library?.type ?? defaultLibraryType(deviceKind.value);
    if (model.value?.source === 'library') return;
    model.value = {
      source: 'library',
      library: {
        type,
        model: libraryFamily(type)?.model ?? 'venetian-blind@1',
        prefs: defaultLibraryPrefs(type, { variant: seedVariant.value }),
      },
      ifc: model.value?.ifc ?? null,
      placement: placement.value,
      driverId: model.value?.driverId ?? null,
    };
    return;
  }
  if (model.value?.source === 'ifc') return;
  // An empty import slot: the model is only placeable once a file is there, so
  // nothing is written until the upload answers (see `onFilePicked`).
  if (model.value?.ifc) {
    model.value = {
      source: 'ifc',
      library: null,
      ifc: model.value.ifc,
      placement: placement.value,
      driverId: model.value?.driverId ?? null,
    };
  } else {
    model.value = null;
  }
}

function pickFamily(type: LibraryModelType, variant?: LuminaireVariant): void {
  const family = libraryFamily(type);
  if (!family) return;
  model.value = {
    source: 'library',
    library: { type, model: family.model, prefs: defaultLibraryPrefs(type, { variant: variant ?? seedVariant.value }) },
    ifc: null,
    placement: placement.value,
    driverId: model.value?.driverId ?? null,
  };
}

/**
 * The fixture the family builds. The stored size overrides belong to the variant
 * they were written for (the schema's `prefs.size` is per-variant), so switching
 * the variant starts on ITS OWN defaults rather than reinterpreting `dia` as a
 * `length`.
 */
function setVariant(value: string): void {
  const current = model.value;
  if (!current?.library) return;
  if (!isLuminaireVariant(value) || value === current.library.prefs.variant) return;
  const mount = current.library.prefs.mount ?? mountForVariant(current.library.prefs.variant ?? 'spot');
  model.value = {
    ...current,
    library: {
      ...current.library,
      prefs: {
        ...current.library.prefs,
        variant: value,
        // The fixture decides its seat when the operator changes the FIXTURE —
        // a wall sconce is not a ceiling light wearing another mesh. A fixture
        // that fits the mount the operator already chose keeps it.
        mount: variantFitsMount(value, mount) ? mount : mountForVariant(value),
        sizeMm: undefined,
      },
    },
  };
}

function setAccent(value: LuminaireAccent): void {
  const current = model.value;
  if (!current?.library || !isLuminaireAccent(value)) return;
  model.value = {
    ...current,
    library: { ...current.library, prefs: { ...current.library.prefs, accent: value } },
  };
}

/** One size override, clamped into its field's range (and only its own keys kept). */
function setSize(key: string, value: number): void {
  const current = model.value;
  if (!current?.library) return;
  if (current.library.prefs.sizeMm?.[key] === value) return; // an emptied field is not a change
  const variant = current.library.prefs.variant ?? 'spot';
  const overrides = { ...(current.library.prefs.sizeMm ?? {}), [key]: value };
  const cleaned = readLuminaireSize(variant, overrides) ?? {};
  model.value = {
    ...current,
    library: {
      ...current.library,
      prefs: { ...current.library.prefs, sizeMm: Object.keys(cleaned).length > 0 ? cleaned : undefined },
    },
  };
}

function setPref(
  key:
    | 'color'
    | 'cassetteHidden'
    | 'slatPitchMm'
    | 'opennessPct'
    | 'rows'
    | 'cols'
    | 'sections'
    | 'leaves'
    | 'shape'
    | 'cct'
    | 'beamDeg',
  value: string | boolean | number,
): void {
  const current = model.value;
  if (!current?.library) return;
  if (current.library.prefs[key] === value) return; // an emptied field is not a change
  model.value = {
    ...current,
    library: { ...current.library, prefs: { ...current.library.prefs, [key]: value } },
  };
}

/**
 * Pick the cistern shape. Only the three the module builds are accepted, so a
 * tampered select value cannot write a shape the renderer has no geometry for.
 */
function setTankShape(event: Event): void {
  const value = (event.target as HTMLSelectElement).value;
  if (!waterTankShape(value)) return;
  setPref('shape', value);
}

function setPlacement(key: 'yawDeg' | 'pitchDeg' | 'widthMm' | 'dropFullMm', value: number): void {
  const current = model.value;
  if (!current) return;
  // Nothing moved: an emptied field (card t_8842b1fe) comes back as the value it
  // already held, and writing that same number would churn the slot — and the
  // device PATCH that follows it — for no reason at all.
  if (current.placement[key] === value) return;
  model.value = { ...current, placement: { ...current.placement, [key]: value } };
}

function setIfcScale(value: number): void {
  const current = model.value;
  if (!current?.ifc || value <= 0) return;
  if (current.ifc.scale === value) return; // an emptied field is not a change
  model.value = { ...current, ifc: { ...current.ifc, scale: value } };
}

/** Drop the imported file from this device (the slot itself stays editable). */
async function removeIfc(): Promise<void> {
  error.value = '';
  try {
    if (props.deviceId) {
      await apiFetchRaw(`/devices/${props.deviceId}/ifc-model`, { method: 'DELETE' });
    }
  } catch (__caught: unknown) {
    // A hub that has no such asset (or an older build without the route) is not
    // an error the operator can act on: the pointer is dropped here regardless,
    // and the renderer stops asking for the file.
    const err = __caught as { data?: { error?: string }; message?: string };
    error.value = err?.data?.error || err?.message || '';
  }
  const current = model.value;
  if (current && current.library) model.value = { ...current, ifc: null };
  else model.value = null;
}
</script>
